import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * Confirm permanent removal of an AI agent from the roster.
 * @param {{
 *   open: boolean,
 *   agent?: object | null,
 *   onClose: () => void,
 *   onRemoved?: () => void,
 * }} props
 */
export default function RemoveAgentDialog({ open, agent, onClose, onRemoved }) {
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (removing) return;
    setError('');
    onClose();
  };

  const handleRemove = async () => {
    if (!agent?.id) return;
    setRemoving(true);
    setError('');
    try {
      await deleteDoc(doc(db, 'ai_agents', agent.id));
      onRemoved?.();
      onClose();
    } catch (e) {
      console.error('RemoveAgentDialog:', e);
      setError(e.message || 'Could not remove agent.');
    } finally {
      setRemoving(false);
    }
  };

  const agentLabel = agent?.name || agent?.roleTitle || 'this agent';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Remove agent?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          <strong>{agentLabel}</strong> will be permanently removed from your AI roster.
          Tickets already assigned to this agent will keep their assignment reference,
          but the agent will no longer appear in the Workforce Roster or assignee pickers.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={removing}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleRemove}
          disabled={removing}
        >
          {removing ? 'Removing…' : 'Remove agent'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
